import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { getProfile, listVisitTempleIds, countTemples, upsertProfile } from "@/lib/yatra-db";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Phone, MapPin, Award, Bell, Save } from "lucide-react";
import { jainArt } from "@/components/jain-icon";

export const Route = createFileRoute("/app/profile")({ component: ProfileScreen });

function ProfileScreen() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [fullName, setFullName] = useState("");
  const [area, setArea] = useState("");
  const [stats, setStats] = useState({ visited: 0, total: 31 });

  useEffect(() => {
    if (!user) {
      return;
    }
    (async () => {
      const pr = getProfile(user.id);
      const vs = listVisitTempleIds(user.id);
      setFullName(pr?.full_name || "");
      setArea(pr?.area || "");
      setStats({ visited: vs.length, total: countTemples() });
    })();
  }, [user]);

  const save = async () => {
    if (!user) {
      return;
    }
    upsertProfile(user.id, { full_name: fullName, area: area || null });
    toast.success("Profile saved");
  };

  const pct = stats.total ? Math.round((stats.visited / stats.total) * 100) : 0;

  return (
    <div className="px-5 pt-16">
      <div className="mb-3">
        <span className="font-serif text-2xl font-bold text-gradient">{t("profile")}</span>
      </div>
      <div
        className="relative overflow-hidden rounded-2xl p-5 text-center text-primary-foreground"
        style={{ background: "var(--gradient-sunset)" }}
      >
        <img
          src={jainArt.mandala}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 opacity-20 mix-blend-screen animate-spin-slow"
        />
        <div className="relative">
          <div
            className="mx-auto flex h-24 w-24 items-center justify-center rounded-full ring-4 ring-white/30 backdrop-blur"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <img src={jainArt.namaste} alt="" aria-hidden className="h-16 w-16 object-contain" />
          </div>
          <div className="mt-3 font-serif text-xl font-bold drop-shadow-sm">
            {fullName || "Yatri"}
          </div>
          <div className="text-xs opacity-90">{user?.phone}</div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-xl bg-white/20 p-2 backdrop-blur">
              <div className="text-base font-bold">{stats.visited}</div>
              <div className="opacity-90">{t("visited")}</div>
            </div>
            <div className="rounded-xl bg-white/20 p-2 backdrop-blur">
              <div className="text-base font-bold">{stats.total - stats.visited}</div>
              <div className="opacity-90">{t("pending")}</div>
            </div>
            <div className="rounded-xl bg-white/20 p-2 backdrop-blur">
              <div className="text-base font-bold">{pct}%</div>
              <div className="opacity-90">{t("certificate")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-card p-4">
        <label className="text-[10px] font-semibold uppercase text-muted-foreground">
          Full Name
        </label>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
        />
        <label className="mt-3 block text-[10px] font-semibold uppercase text-muted-foreground">
          Area
        </label>
        <input
          value={area}
          onChange={(e) => setArea(e.target.value)}
          placeholder="Paldi, Ahmedabad"
          className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
        />
        <button onClick={save} className="btn-primary mt-3 w-full py-2 text-xs">
          <Save className="h-3 w-3" />
          {t("save")}
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {[
          { icon: Phone, label: user?.phone || "—" },
          { icon: MapPin, label: area || "Add your area" },
          { icon: Award, label: t("certificate") },
          { icon: Bell, label: "Notifications" },
        ].map((r) => (
          <div
            key={r.label}
            className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left text-sm"
          >
            <r.icon className="h-4 w-4 text-primary" /> {r.label}
          </div>
        ))}
      </div>
    </div>
  );
}
